import {
  AbstractControl,
  AsyncValidatorFn,
  FormControl,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms'

import { isAfter, isBefore, isToday, isValid, parse, parseISO } from 'date-fns'
import { isBoolean, isEqual } from 'lodash'
import isEmail from 'validator/lib/isEmail'

import { Observable, debounceTime, distinctUntilChanged, first, map, merge, skip } from 'rxjs'

import {
  ComponentTemplateConfig,
  DateBoundSide,
  DatepickerTemplateConfig,
  ExpressionLogMeta,
  FlxValidator,
  FormFieldsEnum,
  NavigationAreaTemplateConfig,
  TemplateConfig,
  ValidatorType,
  expressionHasDynamicVariables,
  isBeyondDateBound,
  parseDateBound,
  parseFiniteNumber,
  resolveAsyncValidator,
  resolveSyncValidatorParam,
  resolveValidator,
  resolveValidatorParam$,
} from '@flowx/core-sdk'

import { expressionLogMeta } from './expression-log-meta'

export type ValidatorFnFactory = (...args: any[]) => ValidatorFn

function isDateString(value?: string): boolean {
  if (!value) {
    return false
  }
  if (
    !/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z/.test(value) &&
    !/\d{4}-\d{2}-\d{2}/.test(value)
  ) {
    return false
  }
  return true
}

export type AsyncValidatorFnFactory = (
  args?: Observable<any>[],
  config?: TemplateConfig,
  logMeta?: ExpressionLogMeta
) => AsyncValidatorFn

export type NgSyncValidator = ValidatorFn | ValidatorFnFactory

export type NgAsyncValidator = AsyncValidatorFn | AsyncValidatorFnFactory

const dateBoundValidator =
  (side: DateBoundSide, errorKey: 'minDate' | 'maxDate') =>
  (bound: unknown, standard: boolean) =>
  (control: AbstractControl): ValidationErrors | null =>
    isBeyondDateBound(control.value, parseDateBound(bound), side, standard)
      ? { [errorKey]: true }
      : null

const CustomValidators = {
  minDate: dateBoundValidator('min', 'minDate'),
  maxDate: dateBoundValidator('max', 'maxDate'),
}

// An unset bound turns the validator off: Validators.maxLength(null) fails every non-empty value.
const boundValidator =
  (validatorFn: (bound: number) => ValidatorFn) =>
  (param: unknown): ValidatorFn => {
    const bound = parseFiniteNumber(param)
    return bound === null ? Validators.nullValidator : validatorFn(bound)
  }

const BoundValidators = {
  min: boundValidator(Validators.min),
  max: boundValidator(Validators.max),
  minLength: boundValidator(Validators.minLength),
  maxLength: boundValidator(Validators.maxLength),
}

// Called even with no params, so an absent bound builds the off validator instead of
// the factory itself landing in the control as a ValidatorFn.
const boundValidatorFactories = new Set<unknown>(Object.values(BoundValidators))

export const FLX_VALIDATORS: Record<string, NgSyncValidator> = {
  required: Validators.required,
  requiredTrue: (control: AbstractControl): ValidationErrors | null =>
    control.value === true ? null : { requiredTrue: true },
  email: (control: AbstractControl): ValidationErrors | null =>
    !control.value || isEmail(control.value) ? null : { email: true },
  pattern: Validators.pattern,
  minlength: BoundValidators.minLength,
  maxlength: BoundValidators.maxLength,
  min: BoundValidators.min,
  max: BoundValidators.max,
  isSameOrBeforeToday: (AC: AbstractControl): ValidationErrors | null => {
    const controlValue = AC.value
    if (!controlValue) {
      return null
    }
    let parsedValue: Date
    if (isDateString(controlValue)) {
      parsedValue = parseISO(controlValue as string)
    } else {
      parsedValue = parse(controlValue as string, 'dd.MM.yyyy', new Date())
    }
    return isValid(parsedValue) && (isBefore(parsedValue, new Date()) || isToday(parsedValue))
      ? null
      : { isSameOrBeforeToday: true }
  },
  isSameOrAfterToday: (AC: AbstractControl): ValidationErrors | null => {
    const controlValue = AC.value
    if (!controlValue) {
      return null
    }
    let parsedValue: Date
    if (isDateString(controlValue)) {
      parsedValue = parseISO(controlValue as string)
    } else {
      parsedValue = parse(controlValue as string, 'dd.MM.yyyy', new Date())
    }
    return isValid(parsedValue) && (isAfter(parsedValue, new Date()) || isToday(parsedValue))
      ? null
      : { isSameOrAfterToday: true }
  },
  minDate: CustomValidators.minDate,
  maxDate: CustomValidators.maxDate,
}

// Params arrive resolved (see resolveValidatorParam$); a bound validator only applies the value.
const boundValueAsyncValidator =
  (bound$: Observable<unknown>, validatorFn: (bound: unknown) => ValidatorFn): AsyncValidatorFn =>
  (fc: AbstractControl): Observable<ValidationErrors | null> =>
    bound$.pipe(
      // A throwaway control runs the sync validator, so errors keep the sync shape.
      map((bound) =>
        bound == null
          ? null
          : new FormControl(fc.value, { validators: [validatorFn(bound)] }).errors
      ),
      debounceTime(0),
      first() // Completes the validator; the live bound$ never does, which would leave the control PENDING.
    )

export const FLX_ASYNC_VALIDATORS: Record<string, AsyncValidatorFnFactory> = {
  min: ([bound$]) => boundValueAsyncValidator(bound$, BoundValidators.min),
  max: ([bound$]) => boundValueAsyncValidator(bound$, BoundValidators.max),
  minlength: ([bound$]) => boundValueAsyncValidator(bound$, BoundValidators.minLength),
  maxlength: ([bound$]) => boundValueAsyncValidator(bound$, BoundValidators.maxLength),
  minDate: ([bound$], config) =>
    boundValueAsyncValidator(bound$, (bound) =>
      CustomValidators.minDate(
        bound,
        (config as DatepickerTemplateConfig).displayOptions.flowxProps.standard
      )
    ),
  maxDate: ([bound$], config) =>
    boundValueAsyncValidator(bound$, (bound) =>
      CustomValidators.maxDate(
        bound,
        (config as DatepickerTemplateConfig).displayOptions.flowxProps.standard
      )
    ),
  // The param arrives evaluated; an empty field value is not skipped.
  customExpression:
    ([verdict$]) =>
    (): Observable<ValidationErrors | null> =>
      verdict$.pipe(
        map((result) => (isBoolean(result) && result ? null : { customExpression: true })),
        debounceTime(0),
        first()
      ),
}

// The min and max date a datepicker enforces, derived from its props, never stored on the config.
export function dateBoundValidators(
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): FlxValidator[] {
  if (
    config.componentIdentifier !== FormFieldsEnum.DATEPICKER ||
    !('displayOptions' in config) ||
    !config.displayOptions.flowxProps
  ) {
    return []
  }

  const {
    minDate,
    maxDate,
    matDatepickerMin,
    matDatepickerMax,
    isComputedMinDate,
    isComputedMaxDate,
    standard,
  } = config.displayOptions.flowxProps

  const bound = (
    name: 'minDate' | 'maxDate',
    value: unknown,
    isComputed: boolean | undefined,
    message: string
  ): FlxValidator => ({
    name,
    type: isComputed ? ValidatorType.ASYNC : ValidatorType.SYNC,
    params: [value, standard],
    isComputedParams: !!isComputed,
    message,
    isCustom: false,
  })

  return [
    ...(minDate ? [bound('minDate', minDate, isComputedMinDate, matDatepickerMin)] : []),
    ...(maxDate ? [bound('maxDate', maxDate, isComputedMaxDate, matDatepickerMax)] : []),
  ]
}

// customExpression only exists async; every other validator keeps its authored type.
const effectiveType = ({ name, type = ValidatorType.SYNC }: FlxValidator): ValidatorType =>
  name === 'customExpression' ? ValidatorType.ASYNC : (type as ValidatorType)

const hasDynamicValues = (param: unknown): boolean =>
  typeof param === 'string' && expressionHasDynamicVariables(param)

const isBuiltInWithDynamicParams = ({ name, params }: FlxValidator): boolean =>
  name in FLX_VALIDATORS && (params ?? []).some(hasDynamicValues)

// A built-in reads its ${…} params at validation time. Custom sync validators keep the
// raw params their authors wrote against (SPEC story 16).
const syncValidator = (
  { name, params }: FlxValidator,
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): ValidatorFn => {
  const validatorOrFactory = resolveValidator(name)
  if (isBuiltInWithDynamicParams({ name, params } as FlxValidator)) {
    return (control) =>
      (validatorOrFactory as ValidatorFnFactory)(
        ...params.map((param: unknown) => resolveSyncValidatorParam(param, config))
      )(control)
  }
  return params?.length > 0 || boundValidatorFactories.has(validatorOrFactory)
    ? (validatorOrFactory as ValidatorFnFactory)(...(params ?? []))
    : (validatorOrFactory as ValidatorFn)
}

const asyncValidator = (
  validator: FlxValidator,
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): AsyncValidatorFn => {
  const { name, params } = validator
  // params$ resolve each param against live process data, so the
  // authored text only survives here — stamp it for the log stream.
  const logMeta = expressionLogMeta(config, {
    logType: 'validator',
    originalExpression: String(params?.[0]),
  })
  const params$: Observable<unknown>[] = (params ?? []).map((param) =>
    resolveValidatorParam$(validator, param, config, logMeta)
  )
  return params$.length
    ? (resolveAsyncValidator(name) as AsyncValidatorFnFactory)(params$, config, logMeta)
    : (resolveAsyncValidator(name) as AsyncValidatorFn)
}

const allValidators = (
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): FlxValidator[] => [
  ...(('validators' in config ? config.validators : undefined) ?? []),
  ...dateBoundValidators(config),
]

// Emits when data a param reads changes, so the control re-validates without user input.
export function validatorParamChanges$(
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): Observable<unknown> {
  const liveParams$ = allValidators(config).flatMap((validator) =>
    effectiveType(validator) === ValidatorType.ASYNC || isBuiltInWithDynamicParams(validator)
      ? (validator.params ?? []).map((param) =>
          resolveValidatorParam$(validator, param, config).pipe(
            distinctUntilChanged(isEqual),
            skip(1)
          )
        )
      : []
  )
  return merge(...liveParams$)
}

export function buildValidators(
  type: ValidatorType,
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): ValidatorFn[] | AsyncValidatorFn[] {
  return allValidators(config)
    .filter((validator) => effectiveType(validator) === type)
    .map((validator) =>
      type === ValidatorType.SYNC
        ? syncValidator(validator, config)
        : asyncValidator(validator, config)
    )
    .filter((validator) => Boolean(validator))
}

export function buildFieldValidators(
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): ValidatorFn[] {
  return buildValidators(ValidatorType.SYNC, config) as ValidatorFn[]
}

export function buildAsyncValidators(
  config: ComponentTemplateConfig | NavigationAreaTemplateConfig
): AsyncValidatorFn[] {
  return buildValidators(ValidatorType.ASYNC, config) as AsyncValidatorFn[]
}
